import { HsCameraType } from '../../engine-new/enums/camera-type.enum';
import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import type { HsObservable } from '../../reactive-new/classes/observable.class';
import { HsSubscription } from '../../reactive-new/classes/subscription.class';
import type { HsSubscriberData } from '../../reactive-new/types/subscriber-data.type';
import { HsCameraToolType } from '../actions/camera-tool-type.enum';
import type { HsControlActionType } from '../actions/control-action-type.enum';
import type { HsControlAction } from '../actions/control-action.abstract';
import { HsCanvasSize } from '../utils/canvas-size.class';
import { HsCameraDefaultMove } from './camera-default-move.class';
import { HsCameraRotationAroundSelectedPoint } from './camera-rotation-around-selected-point.class';
import type { HsCameraTool } from './camera-tool.interface';
import { HsControlActionsHandler } from './control-actions-handler.class';
import { HsCameraRotationAroundTargetObject } from './camera-rotation-around-target-object.class';
import { HsCameraTransitionToTargetPointAndRotate } from './camera-transition-to-target-point-and-rotate.class';
import { HsCameraFocusAt } from './camera-focus-at.class';
import { HsCameraLookAt } from './camera-look-at.class';

export class HsCameraToolsHandler {
  public readonly controlActionsHandler: HsControlActionsHandler = new HsControlActionsHandler();

  private readonly controlActionsMap: Map<HsControlActionType, HsControlAction> =
    this.controlActionsHandler.controlActionsMap;

  public readonly activeCameraToolType$: HsObservable<HsCameraToolType> =
    this.controlActionsHandler.activeCameraToolType$;

  public readonly activeCameraType$: HsObservable<HsCameraType> = this.controlActionsHandler.activeCameraType$;

  public perspectiveCameraToolsMap: Map<HsCameraToolType, HsCameraTool> = new Map<HsCameraToolType, HsCameraTool>();

  public orthographicCameraToolsMap: Map<HsCameraToolType, HsCameraTool> = new Map<HsCameraToolType, HsCameraTool>();

  public activeTool: HsCameraTool | undefined = undefined;

  public activeCameraType: HsCameraType = HsCameraType.Perspective;

  public activeCameraToolType: HsCameraToolType | null = null;

  private readonly canvasSize: HsCanvasSize = new HsCanvasSize(1024, 768);

  private readonly subscription: HsSubscription = new HsSubscription();

  constructor(private readonly camera: HsCamera) {}

  /** The default perspective tool (look, orbit, pan, zoom, keys), once `initialize` created it. */
  public get defaultMove(): HsCameraDefaultMove | undefined {
    const tool: HsCameraTool | undefined = this.perspectiveCameraToolsMap.get(HsCameraToolType.Default);
    return tool instanceof HsCameraDefaultMove ? tool : undefined;
  }

  public initialize(): void {
    this.createCameraTools();
    this.subscription.add(this.processCameraToolTypeChanges());
    this.subscription.add(this.processCameraTypeChanges());
  }

  public dispose(): void {
    this.subscription.unsubscribe();
  }

  public loop(): void {
    if (this.activeTool === undefined) {
      return;
    }

    this.activeTool.processControlActions();
    this.setActionsToInactive();
    this.controlActionsHandler.resetAccumulatedLeftMouseDeltaValues();
  }

  public updateCanvasSize(canvasSize: HsCanvasSize): void {
    this.canvasSize.copy(canvasSize);
  }

  private setActionsToInactive(): void {
    const controlActions: IterableIterator<HsControlAction> = this.controlActionsMap.values();
    for (let index: number = 0; index < this.controlActionsMap.size; index++) {
      const element: IteratorResult<HsControlAction> = controlActions.next();
      const value: HsControlAction = element.value;
      value.setInactive();
    }
  }

  private processCameraToolTypeChanges(): HsSubscriberData<HsCameraToolType> {
    return this.activeCameraToolType$.subscribe((cameraToolType: HsCameraToolType) => {
      this.activeCameraToolType = cameraToolType;
      if (cameraToolType === HsCameraToolType.None) {
        this.activeTool = undefined;
        return;
      }

      switch (this.activeCameraType) {
        case HsCameraType.Perspective:
          this.activeTool = this.perspectiveCameraToolsMap.get(cameraToolType);
          break;
        case HsCameraType.Orthographic:
          this.activeTool = this.orthographicCameraToolsMap.get(cameraToolType);
          break;
        default:
          return;
      }
    });
  }

  private processCameraTypeChanges(): HsSubscriberData<HsCameraType> {
    return this.activeCameraType$.subscribe((activeCameraType: HsCameraType) => {
      if (activeCameraType === undefined) {
        return;
      }

      this.activeCameraType = activeCameraType;
    });
  }

  private createCameraTools(): void {
    this.perspectiveCameraToolsMap.set(
      HsCameraToolType.Default,
      new HsCameraDefaultMove(
        this.camera,
        this.controlActionsMap,
        this.canvasSize,
        this.controlActionsHandler.accumulatedLeftMouseDelta
      )
    );
    this.perspectiveCameraToolsMap.set(
      HsCameraToolType.TargetFocusObject,
      new HsCameraRotationAroundTargetObject(this.camera, this.controlActionsMap, this.canvasSize)
    );
    this.perspectiveCameraToolsMap.set(HsCameraToolType.FocusAt, new HsCameraFocusAt(this.camera));
    this.perspectiveCameraToolsMap.set(HsCameraToolType.LookAt, new HsCameraLookAt(this.camera));
    this.perspectiveCameraToolsMap.set(
      HsCameraToolType.RotateOrbit,
      new HsCameraRotationAroundSelectedPoint(this.camera, this.controlActionsMap, this.canvasSize)
    );
    this.perspectiveCameraToolsMap.set(
      HsCameraToolType.TransitionToTargetPointAndRotate,
      new HsCameraTransitionToTargetPointAndRotate(this.camera)
    );
  }
}
